import * as React from 'react';
import { useHistory, Link } from 'react-router-dom';

import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

import { AnalyticsStore } from 'bernie-core';

import { PzStore, UserAuthStore } from 'stores';

import { UitkHeading } from 'uitk-react-text';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLoader } from 'uitk-react-loader';
import { UitkPrimaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkList, UitkListItem } from 'uitk-react-list';
import { UitkLink } from 'uitk-react-link';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkCheckbox, UitkCheckboxGroup } from 'uitk-react-switch';

import { Program } from 'models';

import { getUserAnalyticsMicroMessage } from '../../util/analytics';

import { LocalizedTextWrapping } from 'components/localized-text-wrapping';
import assignCampaign from 'components/signup/helper-fn/pz-campaign-enrollment-handler';
import updateUserProgramRelationship from 'components/signup/helper-fn/program-relationship-update-handler';
import AffiliateHubLogger from '../../util/affiliate-hub-logger';
import { ClientLogger } from 'bernie-client';

interface JoinProgramFormProps {
  analytics: AnalyticsStore;
  l10n?: ILocalization;
  userAuth?: UserAuthStore;
  pzStore?: PzStore;
  program?: Program;
  partnerId: string;
  accountName: string;
  locale: string;
  campaignTerms?: { terms: string; terms_id: string; state: string };
}
const { getLoggerWithIdentifier } = ClientLogger;
const logger = new AffiliateHubLogger(getLoggerWithIdentifier(`join-program.components`));

export const JoinProgramForm: React.FunctionComponent<JoinProgramFormProps> = ({
  analytics,
  l10n,
  userAuth,
  pzStore,
  partnerId,
  program,
  accountName,
  locale,
  campaignTerms,
}) => {
  const history = useHistory();

  const [status, setStatus] = React.useState(null);
  const [formError, setFormError] = React.useState(null);
  const [agreed, setAgreed] = React.useState(false);

  const pzEnrollForProgramSubmitLabel = l10n.formatText('pz.signup.form.label.enroll');
  const programTermsAndConditionsLabel = l10n.formatText(
    'pz.signup.form.terms_and_conditions.program_terms',
    program.title,
  );

  const unknownServerErrorLabel = l10n.formatText('pz.signup.error.unknown_server_error');

  const rfrrIdProgramJoinApproved = 'affhub_program_join.approved';
  const linkNameProgramJoinApproved = 'AffHub Program Join Approved';

  const redirectToConfirmation = (camref, partnerid, existingRegistration) => {
    const location = {
      pathname: `/${locale}/join-program-confirmation`,
      state: {
        camRef: `${camref}`,
        partnerId: `${partnerid}`,
        existingRegistration,
        program: {
          name: program.name,
          title: program.title,
          locale: program.locale,
          networkTrackingBaseUrl: program.networkTrackingBaseUrl,
          siteUrl: program.siteUrl,
          creativeRef: program.creativeRef,
        },
      },
    };
    history.push(location);
  };

  const triggerEnrollmentForProgramSubmit = async (accessToken) => {
    try {
      const camref = await assignCampaign(
        pzStore,
        logger,
        program.name,
        partnerId,
        campaignTerms.terms_id,
        accessToken,
      );

      if (typeof analytics?.trackEvent === 'function') {
        analytics.trackEvent(rfrrIdProgramJoinApproved, linkNameProgramJoinApproved, null, null, true, [
          getUserAnalyticsMicroMessage(userAuth?.currentUserDetails),
        ]);
      }

      await updateUserProgramRelationship(userAuth, program, partnerId, camref, accountName);
      setStatus(null);
      redirectToConfirmation(camref, partnerId, true);
    } catch (e) {
      handleErrorResponse();
    }
  };

  const handleErrorResponse = () => {
    setFormError(l10n.formatText('common.internal_server_error'));
    window.scrollTo(0, 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('LOADING');
    setFormError(null);
    triggerEnrollmentForProgramSubmit(null);
  };

  return (
    <>
      <UitkSpacing padding={{ blockend: 'three' }}>
        <UitkHeading tag="h4" size={4}>
          <LocalizedText message="pz.signup.existing_registration.title.your_existing_partnerize_account_will_be_used" />
        </UitkHeading>
      </UitkSpacing>
      <UitkSpacing padding={{ blockend: 'six' }}>
        <UitkList>
          <UitkListItem>
            <LocalizedText message="pz.signup.existing_registration.partnerize_partner_id" data={[partnerId]} />
          </UitkListItem>
          <UitkListItem>
            <LocalizedText
              message="pz.signup.existing_registration.partnerize_partner_account_name"
              data={[accountName]}
            />
          </UitkListItem>
        </UitkList>
      </UitkSpacing>

      <form name={'pzProgramEnrollmentForm'} onSubmit={handleSubmit} method="POST" className="JoinProgram__form">
        {formError && (
          <UitkSpacing margin={{ blockend: 'six' }}>
            <div>
              <UitkErrorSummary heading={formError} />
            </div>
          </UitkSpacing>
        )}
        <UitkSpacing margin={{ blockend: 'six' }}>
          <div>
            <UitkSpacing margin={{ blockstart: 'four', blockend: 'three' }}>
              <UitkHeading tag="h4" size={5}>
                <LocalizedText message="pz.signup.form.terms_and_conditions.title.review_our_terms_and_conditions" />
              </UitkHeading>
            </UitkSpacing>
            <div className={`PzCheckbox ${campaignTerms?.state === 'pending' ? 'PzCheckbox--pending' : ''}`}>
              <UitkLoader isSmall={true} className="PzCheckbox__spinner" />
              <UitkCheckbox
                id="pzSignupProgramTc"
                data-stid="pzSignupProgramTc"
                label={programTermsAndConditionsLabel}
                disabled={!(campaignTerms?.terms?.length > 0)}
                checked={agreed}
                onChange={() => {
                  setAgreed(!agreed);
                }}
              >
                <LocalizedTextWrapping
                  message="pz.signup.form.terms_and_conditions.i_have_read_and_accept_the_program_terms"
                  fieldName="termsAndConditions"
                >
                  <UitkLink inline={true}>
                    <Link
                      to={{
                        pathname: `/${locale}/entity-terms-and-conditions`,
                        search: `?programName=${program.name}`,
                      }}
                      target={'_blank'}
                    >
                      <LocalizedText
                        message="pz.signup.form.terms_and_conditions.program_terms"
                        data={[program.title]}
                      />
                    </Link>
                  </UitkLink>
                </LocalizedTextWrapping>
              </UitkCheckbox>
            </div>
          </div>
        </UitkSpacing>
        <UitkPrimaryButton
          data-cy="enroll-to-program-submit"
          id="pzEnrollForProgramSubmitButton"
          data-stid="pzEnrollForProgramSubmitButton"
          type="submit"
          disabled={!agreed}
          size={UitkButtonSize.LARGE}
        >
          {status === 'LOADING' ? <UitkLoader isSmall={true} isDarkBackground={true} /> : pzEnrollForProgramSubmitLabel}
        </UitkPrimaryButton>
      </form>
    </>
  );
};
